/**
 * PG 游戏启动弹窗
 * 半屏底部弹窗，展示游戏信息 + 正版认证提示 + 启动按钮
 */
import { Dialog } from '@base-ui-components/react/dialog';
import { cn } from '@/utils/cn';
import Button from '@/components/ui/Button';
import { buildPgLaunchUrl, type PgLaunchTarget } from './pgLaunchUrl';

export interface PgLaunchGame extends PgLaunchTarget {
  name: string;
  iconUrl?: string;
  gameId?: number;
  /** 是否为大厅入口 */
  isLobby?: boolean;
  /** 是否为锦标赛入口 */
  isTournament?: boolean;
}

interface PgLaunchSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  game: PgLaunchGame | null;
  groupId?: number;
}

export default function PgLaunchSheet({
  open,
  onOpenChange,
  game,
  groupId,
}: PgLaunchSheetProps) {
  if (!game) return null;

  const handleLaunch = () => {
    window.open(buildPgLaunchUrl(game, groupId), '_blank');
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        {/* 遮罩层 */}
        <Dialog.Backdrop
          className={cn(
            'fixed inset-0 min-h-dvh bg-black/50',
            'z-[100]',
            'transition-opacity duration-300',
            'data-[starting-style]:opacity-0',
            'data-[ending-style]:opacity-0'
          )}
        />

        {/* 底部弹窗 */}
        <Dialog.Popup
          className={cn(
            'fixed bottom-0 left-1/2 -translate-x-1/2',
            'w-full max-w-[460px]',
            'bg-bg-card rounded-t-2xl',
            'shadow-[0_-4px_16px_rgba(0,0,0,0.1)]',
            'transition-transform duration-300 ease-out',
            'data-[starting-style]:translate-y-full',
            'data-[ending-style]:translate-y-full',
            'flex flex-col',
            'outline-none',
            'pb-safe',
            'z-[101]'
          )}
        >
          {/* 拖动指示条 */}
          <div className="flex justify-center pt-3 pb-2 shrink-0">
            <div className="w-10 h-1 bg-border-color rounded-full" />
          </div>

          {/* 内容区域 */}
          <div className="px-5 pb-5 flex flex-col gap-3">
            {/* 游戏信息：左图标 + 右信息 */}
            <div className="flex items-center gap-4">
              {/* 游戏图标 */}
              <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 shadow-md">
                {game.iconUrl ? (
                  <img
                    src={game.iconUrl}
                    alt={game.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                    <span className="text-white text-xl font-bold">PG</span>
                  </div>
                )}
              </div>
              {/* 游戏名称和描述 */}
              <div className="flex-1 min-w-0">
                <Dialog.Title className="text-lg font-semibold text-text-primary truncate">
                  {game.name}
                </Dialog.Title>
                <p className="text-sm text-text-secondary mt-0.5">
                  PG SOFT™ {game.isLobby ? '游戏大厅' : game.isTournament ? '锦标赛' : '正版游戏'}
                </p>
              </div>
            </div>

            {/* 正版认证信息卡片 */}
            <div className="bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 rounded-xl overflow-hidden border border-purple-100/80">
              {/* 卡片头部 */}
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-4 py-2.5 flex items-center gap-2">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-white text-xs font-semibold tracking-wide">PG SOFT™ 官方正版认证</span>
              </div>
              {/* 卡片内容 */}
              <div className="px-4 py-3">
                <p className="text-xs text-gray-600 leading-relaxed">
                  PG SOFT™ 产品已通过国际权威认证机构 BMM 和 GA 的双重严格认证和测试，确保了游戏的 RNG 算法和实施，所有游戏的数学运算和规则皆符合行业最高标准和要求，保证公正与公平。
                </p>
                {/* 认证机构 + 特性 */}
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex-1 flex flex-col gap-1.5">
                    <div className="flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-[11px] text-gray-600">RNG 公平公正</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-[11px] text-gray-600">数据安全加密</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-[11px] text-gray-600">交易可验证追溯</span>
                    </div>
                  </div>
                  {/* 认证机构标签列 */}
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] text-purple-700 bg-purple-100 px-2 py-0.5 rounded text-center">Gambling Commission</span>
                    <span className="text-[10px] text-purple-700 bg-purple-100 px-2 py-0.5 rounded text-center">MGA</span>
                    <span className="text-[10px] text-purple-700 bg-purple-100 px-2 py-0.5 rounded text-center">BMM Testlabs</span>
                  </div>
                </div>
                {/* 了解更多链接 */}
                <a
                  href="/pg-verification"
                  className="mt-3 flex items-center justify-center gap-1.5 text-xs text-purple-600 font-medium bg-purple-50 hover:bg-purple-100 rounded-lg py-2 transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  了解正版验证详情
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            </div>

            {/* 启动按钮 */}
            <Button
              variant="primary"
              block
              onClick={handleLaunch}
              className="h-12 text-base font-semibold rounded-xl"
            >
              启动游戏
            </Button>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
